/**
 * The in-flight signup session, persisted so a page reload does not kill the
 * flow (concern W1).
 *
 * The app keeps sessionId / stage / payloads in navigation params, which
 * survive backgrounding. A browser reload wipes React state, so the web has
 * to persist it. `sessionStorage` is deliberate: it is per-tab, so signing up
 * in one tab and logging in in another cannot fight over it (W3), and it dies
 * with the tab.
 *
 * Expiry is enforced here rather than trusted from the server, because the
 * session TTL is not echoed in any response yet.
 */

import { AUTH_SESSION_TTL_SECONDS } from "./authPolicy";

const SIGNUP_KEY = "sayagie.auth.signup";

/**
 * @typedef {Object} SignupSession
 * @property {string|null} sessionId
 * @property {import("./authTypes").VerificationStage} stage
 * @property {string} contact            What the user typed, for display.
 * @property {import("./authTypes").ChannelType} channelType
 * @property {string} [dialCode]
 * @property {Object} [detailsPayload]
 * @property {Object} [profileDraft]
 * @property {number} startedAt          Epoch ms.
 */

const isExpired = (session) =>
  !session?.startedAt ||
  Date.now() - session.startedAt > AUTH_SESSION_TTL_SECONDS * 1000;

/**
 * @returns {SignupSession|null} Null when absent or expired. An expired
 *   session is cleared as a side effect, so the caller can send the user back
 *   to Get Started with the session-expired copy.
 */
export const readSignupSession = () => {
  try {
    const raw = sessionStorage.getItem(SIGNUP_KEY);
    if (!raw) return null;

    const session = JSON.parse(raw);

    if (isExpired(session)) {
      clearSignupSession();
      return null;
    }

    return session;
  } catch {
    return null;
  }
};

/** Starts a fresh session, stamping `startedAt`. */
export const beginSignupSession = (session) => {
  try {
    sessionStorage.setItem(
      SIGNUP_KEY,
      JSON.stringify({ ...session, startedAt: Date.now() })
    );
  } catch {
    /* storage unavailable — the flow still works until a reload */
  }
};

/**
 * Merges into the stored session, preserving `startedAt` so a resend does not
 * extend the TTL.
 */
export const patchSignupSession = (patch) => {
  const current = readSignupSession();
  if (!current) return null;

  const next = { ...current, ...patch };

  try {
    sessionStorage.setItem(SIGNUP_KEY, JSON.stringify(next));
  } catch {
    /* as above */
  }

  return next;
};

export const clearSignupSession = () => {
  try {
    sessionStorage.removeItem(SIGNUP_KEY);
  } catch {
    /* nothing to do */
  }
};
